import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  OnInit,
  Output
} from '@angular/core'
import { Router } from '@angular/router'
import { ChangeContext, Options } from '@angular-slider/ngx-slider'
import { RoutingService } from '@spartacus/core'
import { FacetComponent } from '@spartacus/storefront'
import { Subscription } from 'rxjs'
import { take } from 'rxjs/operators'
import {
  EbFacet,
  EbFacetSelectionTypeEnum,
  EbFacetValue
} from '../../../interfaces/eb-product.interface'
import { EbFacetService } from './eb-facet.service'

@Component({
    selector: 'eb-facet',
    templateUrl: './eb-facet.component.html',
    styleUrls: ['./eb-facet.component.scss'],
    standalone: false
})
export class EbFacetComponent extends FacetComponent implements OnInit {
  @Output() beforeFacetChange = new EventEmitter<number>()
  rangeActive = false
  subscriptions = new Subscription()

  constructor(
    protected facetService: EbFacetService,
    elementRef: ElementRef<HTMLElement>,
    cd: ChangeDetectorRef,
    protected routingService: RoutingService,
    private readonly router: Router
  ) {
    super(facetService, elementRef, cd)
  }

  ngOnInit() {
    this.routingService
      .getRouterState()
      .pipe(take(1))
      .subscribe(state => {
        this.rangeActive = this.facetService.checkQueryHasFacet(
          state.state.params.query,
          this.facet
        )
      })
  }

  get facet() {
    return super.facet as EbFacet
  }

  set facet(facet: EbFacet) {
    super.facet = facet
  }

  get SelectionType() {
    return EbFacetSelectionTypeEnum
  }

  get ngxOptions() {
    const sorted = this.sortedValues
    return {
      floor: parseFloat(sorted[0].code),
      ceil: parseFloat(sorted.slice(-1)[0].code),
      stepsArray: sorted.map(facetValue => ({
        value: parseFloat(facetValue.code),
        legend: facetValue.name
      })),
      translate: (value: number, labelType) => value + (this.facet.unit || '')
    } as Options
  }

  get lowValue(): number {
    return parseFloat(this.selectedRangeValues[0].code)
  }

  get highValue(): number {
    return parseFloat(this.selectedRangeValues.slice(-1)[0].code)
  }

  get selectedValues() {
    return [...this.sortedValues].filter(value => value.selected)
  }

  get selectedRangeValues(): EbFacetValue[] {
    const selected = this.selectedValues
    if (selected.length === 0 && this.sortedValues.length) {
      selected.push(this.sortedValues[0], this.sortedValues.slice(-1)[0])
    }
    if (selected.length === 1) {
      selected.push(selected[0])
    }
    return selected
  }

  get sortedValues(): EbFacetValue[] {
    return [...this.facet.values].sort((a, b) => (parseFloat(a.code) > parseFloat(b.code) ? 1 : -1))
  }

  get isExpanded() {
    return this.facet.selectionType === EbFacetSelectionTypeEnum.MULTISELECT && super.isExpanded
  }

  get queryWithoutFacet() {
    return this.facetService.getQueryWithoutFacet(this.facet)
  }

  get getScrollPosition() {
    return this.elementRef.nativeElement.getBoundingClientRect().top
  }

  toggleRange() {
    this.rangeActive = !this.rangeActive
    this.rangeChange()
  }

  rangeChange(changeContext?: ChangeContext) {
    if (changeContext) {
      this.rangeActive = true
    }
    let query = this.facetService.getQueryWithoutFacet(this.facet)
    if (this.rangeActive) {
      const { code } = this.facet
      const values = this.getFacetValuesOfRangeChange(changeContext)
      if (query && values.length) {
        query += `:${code}:${values.map(value => value.code).join('|')}`
      }
    }
    this.request(query)
  }

  singleChange(event) {
    let query = this.facetService.getQueryWithoutFacet(this.facet)
    if (query !== event.target.value) {
      query += `:${this.facet.code}:${event.target.value}`
    }
    this.request(query)
  }

  private getFacetValuesOfRangeChange(changeContext?: ChangeContext) {
    const values = this.sortedValues
    if (changeContext) {
      const low = values.find(value => parseFloat(value.code) === changeContext.value)
      const high = values.find(value => parseFloat(value.code) === changeContext.highValue)
      const lowIndex = values.indexOf(low)
      const highIndex = values.indexOf(high)
      return values.slice(lowIndex, highIndex + 1)
    }
    return values
  }

  private request(query) {
    this.beforeFacetChange.emit(this.getScrollPosition)
    const category = this.router.url.split('/').pop().split(/\?/)[0]
    this.router.navigate(['./c/' + category], { queryParams: { query } })
  }
}
